import { useAuth0 } from "@auth0/auth0-react"
import { type DropdownMenuEntries } from "@kamae-apps/shared/Components/Molecules/Dropdown/Menu/Menu"
import { useIsPageScrolling } from "@kamae-apps/shared/Hooks/useIsPageScrolling"
import { COMPANY_STATUS } from "@kamae-apps/shared/Types/Company"
import { SCOPE, scopeHas } from "@kamae-apps/shared/Types/Scope"
import { useCryptr } from "@killiangabrielkamae/cryptr-react"
import type { TFunction } from "i18next"
import posthog from "posthog-js"
import { useCallback, useMemo, useState } from "react"
import { useAuth } from "react-oidc-context"
import { useNavigate } from "react-router-dom"
import { SidebarMode, useSidebarMode } from "../../../Contexts/SidebarMode/SidebarModeContext"
import { useUser } from "../../../Contexts/User/UserContext"
import { useAvatars } from "../../../hooks/useAvatars"
import config from "../../../variable"

export const useMenu = (t: TFunction) => {
  const auth0 = useAuth0()
  const cryptr = useCryptr()
  const oidcAxa = useAuth()
  const { user } = useUser()
  const navigate = useNavigate()
  const { mode } = useSidebarMode()
  const isMobile = mode === SidebarMode.MOBILE
  const { getAvatarUrl, getRoundedAvatarUrl } = useAvatars()
  const isAdmin = scopeHas(user.scope, SCOPE.COMPANY_ADMIN)
  const isTeamLeader = user.id === user.team.leader?.id
  const isStarterCompany = user.company.status === COMPANY_STATUS.STARTER
  const [starterModal, setStarterModal] = useState(false)
  const [displayWelcomeModal, setDisplayWelcomeModal] = useState(false)
  const { isPageScrolling } = useIsPageScrolling()

  const logOut = useCallback(() => {
    posthog.reset()
    localStorage.clear()
    if (cryptr.isAuthenticated()) {
      cryptr.logOut(undefined, `${config.app}/login`, false)
    } else if (auth0.isAuthenticated) {
      auth0.logout({ returnTo: `${window.location.origin}/login` })
    } else if (oidcAxa.isAuthenticated) {
      // Remove local session only (do not SLO)
      oidcAxa.removeUser()
      window.location.assign("/login")
    }
  }, [auth0, cryptr, oidcAxa])

  const baseMenuEntries: DropdownMenuEntries[] = useMemo(() => {
    const entries: DropdownMenuEntries[] = [
      {
        content: {
          displayNameKey: t("app:userMenu.avatar"),
          iconName: "pen",
          onClick: () => navigate("/profile"),
        },
      },
      {
        content: {
          displayNameKey: t("app:userMenu.tutorial"),
          iconName: "refreshBack",
          onClick: () => setDisplayWelcomeModal(true),
        },
      },
      { divider: true },
      {
        content: {
          isDanger: true,
          displayNameKey: t("app:userMenu.logout"),
          iconName: "logOut",
          onClick: logOut,
        },
      },
    ]
    return entries
  }, [t, navigate, logOut])

  const adminMenuEntries: DropdownMenuEntries[] = useMemo(
    () =>
      isAdmin && isMobile
        ? [
            {
              content: {
                displayNameKey: t("app:userMenu.dashboard"),
                iconName: "stat",
                onClick: () => navigate("/dashboard"),
              },
            },
            {
              content: {
                onClick: () => navigate("/administration"),
                iconName: "settings",
                displayNameKey: t("administration:sidebar.label"),
              },
            },
          ]
        : [],
    [isAdmin, isMobile, navigate, t]
  )
  const userMenuEntries: DropdownMenuEntries[] = useMemo(
    () => [...adminMenuEntries, ...baseMenuEntries],
    [adminMenuEntries, baseMenuEntries]
  )

  return {
    user,
    mode,
    isAdmin,
    isTeamLeader,
    userMenuEntries,
    displayWelcomeModal,
    setDisplayWelcomeModal,
    getAvatarUrl,
    getRoundedAvatarUrl,
    isStarterCompany,
    starterModal,
    setStarterModal,
    isPageScrolling,
  }
}
